import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";

function pickRecordingMimeType() {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "audio/webm";
  }
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4",
  ];
  const supported = candidates.find((type) => window.MediaRecorder.isTypeSupported?.(type));
  return supported || "audio/webm";
}

function fileExtensionFromMimeType(mimeType) {
  const normalized = String(mimeType || "").toLowerCase();
  if (normalized.includes("wav")) return ".wav";
  if (normalized.includes("ogg")) return ".ogg";
  if (normalized.includes("mp4") || normalized.includes("m4a")) return ".m4a";
  if (normalized.includes("mpeg") || normalized.includes("mp3")) return ".mp3";
  return ".webm";
}

function formatDuration(ms) {
  if (!ms) return "0:00";
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function B1SpeakingAudioRecorder({
  attemptId,
  currentAudio,
  speakingStatus,
  invalidReason,
  prompts,
  disabled,
  onUpload,
}) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [localError, setLocalError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [durationMs, setDurationMs] = useState(currentAudio?.duration_ms || 0);
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [permissionState, setPermissionState] = useState("unbekannt");
  const [recordingStartedAt, setRecordingStartedAt] = useState(null);

  useEffect(() => {
    setDurationMs(currentAudio?.duration_ms || 0);
  }, [currentAudio?.duration_ms]);

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
  }, [audioUrl]);

  useEffect(() => {
    if (!isRecording || !recordingStartedAt) return undefined;
    const timer = window.setInterval(() => {
      setDurationMs(Date.now() - recordingStartedAt);
    }, 250);
    return () => window.clearInterval(timer);
  }, [isRecording, recordingStartedAt]);

  const canSubmitAudio = useMemo(() => {
    return Boolean(blob) && durationMs >= 12000 && durationMs <= 180000 && !uploading && !disabled;
  }, [blob, durationMs, uploading, disabled]);

  async function startRecording() {
    setLocalError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionState("erteilt");
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: pickRecordingMimeType() });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const nextBlob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setBlob(nextBlob);
        setAudioUrl(URL.createObjectURL(nextBlob));
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };
      setBlob(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      setDurationMs(0);
      setRecordingStartedAt(Date.now());
      setIsRecording(true);
      recorder.start();
    } catch (error) {
      setPermissionState("verweigert");
      setLocalError(error?.message || "Mikrofonzugriff konnte nicht gestartet werden.");
    }
  }

  function stopRecording() {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    setRecordingStartedAt(null);
  }

  function redoRecording() {
    setLocalError("");
    setBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setDurationMs(currentAudio?.duration_ms || 0);
  }

  async function uploadAudio() {
    if (!blob || !attemptId) return;
    setUploading(true);
    setLocalError("");
    try {
      const mimeType = blob.type || "audio/webm";
      const file = new File([blob], `speaking-${attemptId}${fileExtensionFromMimeType(mimeType)}`, { type: mimeType });
      await onUpload(file, durationMs);
      setBlob(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
    } catch (error) {
      setLocalError(error?.message || "Die Audioaufnahme konnte nicht hochgeladen werden.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
        <p className="text-xs uppercase tracking-[0.14em] text-cyan-200">Sprechaufgabe</p>
        <ul className="mt-3 space-y-2 text-slate-200">
          {(prompts || []).map((prompt) => (
            <li key={prompt}>• {prompt}</li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-white">Audioaufnahme</p>
            <p className="mt-1 text-sm text-slate-300">
              Nimm deine Antwort als echte Sprachaufnahme auf. Mindestdauer 12 Sekunden, maximal 3 Minuten.
            </p>
          </div>
          <div className="text-sm text-slate-300">
            <span className="font-semibold text-white">Dauer:</span> {formatDuration(durationMs)}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {!isRecording ? (
            <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={startRecording} disabled={disabled || uploading}>
              Aufnahme starten
            </Button>
          ) : (
            <Button className="bg-rose-500 text-white hover:bg-rose-400" onClick={stopRecording}>
              Aufnahme stoppen
            </Button>
          )}
          <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={redoRecording} disabled={isRecording || uploading || (!blob && !audioUrl)}>
            Neu aufnehmen
          </Button>
          <Button className="bg-emerald-400 text-slate-950 hover:bg-emerald-300" onClick={uploadAudio} disabled={!canSubmitAudio}>
            {uploading ? "Lade hoch..." : "Audio hochladen"}
          </Button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 text-sm text-slate-200">
            <p className="font-semibold text-white">Mikrofonstatus</p>
            <p className="mt-1">{permissionState}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 text-sm text-slate-200">
            <p className="font-semibold text-white">Systemstatus</p>
            <p className="mt-1">{speakingStatus || "not_started"}</p>
          </div>
        </div>

        {audioUrl ? (
          <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/40 p-3">
            <p className="mb-2 text-sm font-semibold text-white">Wiedergabe vor Abgabe</p>
            <audio controls src={audioUrl} className="w-full" />
          </div>
        ) : null}

        {currentAudio?.uploaded && !audioUrl ? (
          <div className="mt-4 rounded-xl border border-emerald-300/30 bg-emerald-500/10 p-3 text-sm text-emerald-100">
            Letzte gespeicherte Aufnahme vorhanden: {currentAudio.original_name || "Audio gespeichert"} · {formatDuration(currentAudio.duration_ms)}
          </div>
        ) : null}

        {invalidReason ? (
          <div className="mt-4 rounded-xl border border-amber-300/30 bg-amber-500/10 p-3 text-sm text-amber-100">
            Audio ungueltig: {invalidReason}. Bitte neu aufnehmen.
          </div>
        ) : null}
        {localError ? (
          <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-100">
            {localError}
          </div>
        ) : null}
      </div>
    </div>
  );
}
